import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Horse, BoxCell } from './types';

// Set up fonts for pdfMake
(pdfMake as any).vfs = pdfFonts;

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const generateBoxPlanPDF = (
  horses: Horse[],
  layout: BoxCell[][],
  ownerNumberMap: Map<string, string>
) => {
  const today = formatDate(new Date());

  const docDefinition: any = {
    pageOrientation: 'landscape',
    pageSize: 'A4',
    pageMargins: [15, 50, 15, 50],

    header: () => {
      return {
        margin: [20, 10, 20, 10],
        columns: [
          {
            width: '*',
            text: 'Reitsport Walther & Finger GbR\nBarriser Weg 64\n24787 Fockbek',
            style: 'company'
          },
          {
            width: 150,
            text: today + '\nTel.: 01523/3697043\nmail: waltherj@web.de',
            style: 'company',
            alignment: 'right'
          }
        ]
      };
    },

    content: [
      {
        text: 'Boxenbelegung ' + new Date().getFullYear(),
        style: 'title',
        margin: [0, 0, 0, 10]
      },

      {
        text: 'Die Nummer vor dem Pferdenamen ist der Besitzer',
        style: 'noteText',
        margin: [0, 0, 0, 8]
      },

      // Stallplan als Tabelle
      createStableLayoutTable(horses, ownerNumberMap),

      // Legende
      {
        margin: [0, 10, 0, 0],
        columns: [
          {
            width: '50%',
            stack: [
              { text: 'Besitzer', style: 'legendHeader' },
              {
                table: {
                  widths: ['auto', '*', 'auto', '*'],
                  body: createOwnerLegend(ownerNumberMap)
                },
                layout: 'noBorders'
              }
            ]
          },
          {
            width: '50%',
            stack: [
              { text: 'Legende', style: 'legendHeader' },
              {
                table: {
                  widths: [30, '*'],
                  body: [
                    [
                      {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 25,
                            h: 15,
                            color: '#ffeb3b'
                          }
                        ]
                      },
                      { text: 'Einsteller', margin: [5, 2, 0, 0] }
                    ],
                    [
                      {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 25,
                            h: 15,
                            color: '#ff9800'
                          }
                        ]
                      },
                      { text: 'Leere Box', margin: [5, 2, 0, 0] }
                    ],
                    [
                      {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 25,
                            h: 15,
                            color: '#2196f3'
                          }
                        ]
                      },
                      { text: 'Sonderbereiche', margin: [5, 2, 0, 0] }
                    ],
                    [
                      {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 25,
                            h: 15,
                            color: '#4caf50'
                          }
                        ]
                      },
                      { text: 'Offenstall (O1/O2)', margin: [5, 2, 0, 0] }
                    ],
                    [
                      {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 25,
                            h: 15,
                            color: '#795548'
                          }
                        ]
                      },
                      { text: 'Lamastall (O3/O4)', margin: [5, 2, 0, 0] }
                    ]
                  ]
                },
                layout: 'noBorders'
              }
            ]
          }
        ]
      },

      {
        text: 'Änderungen auch kurzfristig möglich',
        style: 'note',
        margin: [0, 8, 0, 0]
      }
    ],

    footer: (currentPage: number, pageCount: number) => {
      return {
        margin: [20, 10, 20, 10],
        columns: [
          {
            width: '*',
            text:
              'Reitsport Walther & Finger GbR\nGeschäftsführer\nDr. Lutz Finger und Joachim Walther',
            style: 'companySmall'
          },
          {
            width: '*',
            text:
              'Raiffeisenbank im Kreis Rendsburg\nIBAN DE72 2169 0020 0005 4678 02\nBIC GENO DEF 1 NTO',
            style: 'companySmall'
          },
          {
            width: '*',
            text: 'Finanzamt Rendsburg\nStnr. 028/226/06957',
            style: 'companySmall'
          }
        ]
      };
    },

    styles: {
      title: {
        fontSize: 18,
        bold: true,
        color: '#ee0000',
        alignment: 'center'
      },
      company: {
        fontSize: 8,
        color: 'black'
      },
      companySmall: {
        fontSize: 7,
        color: 'grey',
        alignment: 'center'
      },
      legendHeader: {
        fontSize: 11,
        bold: true,
        color: '#00695c',
        margin: [0, 0, 0, 5]
      },
      note: {
        fontSize: 8,
        color: 'red',
        bold: true,
        alignment: 'center'
      },
      noteText: {
        fontSize: 8,
        color: 'red',
        bold: true,
        alignment: 'center',
        italics: true
      },
      boxText: {
        fontSize: 7,
        alignment: 'center'
      },
      ownerNumber: {
        fontSize: 9,
        bold: true,
        alignment: 'center'
      },
      horseName: {
        fontSize: 7,
        alignment: 'center'
      }
    }
  };

  pdfMake.createPdf(docDefinition).open();
};

function createStableLayoutTable(
  horses: Horse[],
  ownerNumberMap: Map<string, string>
): any {
  // Determine if a horse should be visible based on hidden status and contract end date
  const isHorseVisible = (horse: Horse): boolean => {
    // If not hidden, always visible
    if (!horse.hidden) return true;

    // If hidden, check if contract end date is in current month or future
    if (horse.contract?.end_date) {
      const endDate = new Date(horse.contract.end_date);
      const today = new Date();

      // Get year-month for comparison (year * 12 + month gives comparable values)
      const endYearMonth = endDate.getFullYear() * 12 + endDate.getMonth();
      const currentYearMonth = today.getFullYear() * 12 + today.getMonth();

      // Visible if contract ends in current month or later
      return endYearMonth >= currentYearMonth;
    }

    // If hidden and no end date, hide it
    return false;
  };

  const getHorseByBoxNumber = (boxNumber: string): Horse | undefined => {
    return horses.find(h => h.boxNumber === boxNumber && isHorseVisible(h));
  };

  const getHorsesByBoxNumber = (boxNumber: string): Horse[] => {
    return horses.filter(h => h.boxNumber === boxNumber && isHorseVisible(h));
  };

  const createBoxCell = (boxNumber: string, label?: string, rowSpan?: number, colSpan?: number): any => {
    const boxHorses = getHorsesByBoxNumber(boxNumber);

    // Determine fill color based on box type
    let fillColor = '#ffeb3b'; // Default yellow for occupied
    let emptyFillColor = '#ff9800'; // Default orange for empty

    // Special colors for O boxes
    if (boxNumber === 'O1' || boxNumber === 'O2') {
      fillColor = '#4caf50'; // Green for Offenstall (occupied)
      emptyFillColor = '#4caf50'; // Green for Offenstall (empty)
    } else if (boxNumber === 'O3' || boxNumber === 'O4') {
      fillColor = '#7c6359'; // Brown for Lamastall (occupied)
      emptyFillColor = '#7c6359'; // Brown for Lamastall (empty)
    }

    if (boxHorses.length > 0) {
      // Multiple horses: create a stack with all horses
      const horseStacks = boxHorses.map(horse => {
        const ownerNumber = horse.contract?.owner_name
          ? ownerNumberMap.get(horse.contract.owner_name)
          : undefined;

        return {
          stack: [
            {
              text: ownerNumber || '',
              style: 'ownerNumber'
            },
            {
              text: horse.name,
              style: 'horseName'
            }
          ],
          margin: [0, 0.5, 0, 0.5]
        };
      });

      return {
        stack: horseStacks,
        fillColor: fillColor,
        border: [true, true, true, true],
        margin: 1,
        rowSpan: rowSpan || 1,
        colSpan: colSpan || 1,
        fontSize: 6,  // Smaller font for boxes with multiple horses
        noWrap: false,  // Allow text wrapping
        color: (boxNumber === 'O1' || boxNumber === 'O2' || boxNumber === 'O3' || boxNumber === 'O4') ? 'white' : 'black'  // White text for O boxes
      };
    } else {
      return {
        text: label || boxNumber,
        style: 'boxText',
        fillColor: emptyFillColor,
        border: [true, true, true, true],
        margin: 1,
        rowSpan: rowSpan || 1,
        colSpan: colSpan || 1,
        color: (boxNumber === 'O1' || boxNumber === 'O2' || boxNumber === 'O3' || boxNumber === 'O4') ? 'white' : undefined,  // White text for O boxes
        bold: (boxNumber === 'O1' || boxNumber === 'O2' || boxNumber === 'O3' || boxNumber === 'O4')  // Bold for O boxes
      };
    }
  };

  const specialCell = (label: string, color: string, rowSpan?: number, colSpan?: number): any => {
    return {
      text: label,
      style: 'boxText',
      bold: true,
      fillColor: color,
      border: [true, true, true, true],
      margin: 1,
      rowSpan: rowSpan || 1,
      colSpan: colSpan || 1
    };
  };

  const emptyCell = (): any => ({ text: '', fillColor: '#b1b1ba' ,border: [false, false, false, false], margin: 1 });
  const placeholder = (): any => ({});  // For colspan/rowspan placeholders

  const tableBody: any[] = [];

  // Row 1: A1-A12 (12 boxes, each spanning 2 rows)
  tableBody.push([
    createBoxCell('A1', undefined, 2),
    createBoxCell('A2', undefined, 2),
    createBoxCell('A3', undefined, 2),
    createBoxCell('A4', undefined, 2),
    createBoxCell('A5', undefined, 2),
    createBoxCell('A6', undefined, 2),
    createBoxCell('A7', undefined, 2),
    createBoxCell('A8', undefined, 2),
    createBoxCell('A9', undefined, 2),
    createBoxCell('A10', undefined, 2),
    createBoxCell('A11', undefined, 2),
    createBoxCell('A12', undefined, 2)
  ]);

  // Row 2: A1-A12 continue (placeholders)
  tableBody.push([
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder(),
    placeholder()
  ]);

  // Row 3: Spacer row below A1-A12
  tableBody.push([
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell()
  ]);

  // Row 4 (1st of 6): B1-B4, B5, Stairs, C1-C2 (most span 2 rows, Stairs 1 row)
  tableBody.push([
    emptyCell(),
    emptyCell(),
    createBoxCell('B1', undefined, 2),  // Span 2 rows
    createBoxCell('B2', undefined, 2),  // Span 2 rows
    createBoxCell('B3', undefined, 2),  // Span 2 rows
    createBoxCell('B4', undefined, 2),  // Span 2 rows
    emptyCell(),
    createBoxCell('B5', undefined, 2),  // Span 2 rows
    specialCell('Treppe', '#673ab7', 1),  // Only 1 row
    emptyCell(),
    createBoxCell('C1', undefined, 2),  // Span 2 rows
    createBoxCell('C2', undefined, 2)   // Span 2 rows
  ]);

  // Row 5 (2nd of 6): B1-B4, B5, C1-C2 continue, D1 starts (span 2)
  tableBody.push([
    emptyCell(),
    emptyCell(),
    placeholder(),  // B1 continues
    placeholder(),  // B2 continues
    placeholder(),  // B3 continues
    placeholder(),  // B4 continues
    emptyCell(),
    placeholder(),  // B5 continues
    createBoxCell('D1', undefined, 2),  // D1 starts, spans 2 rows
    emptyCell(),
    placeholder(),  // C1 continues
    placeholder()   // C2 continues
  ]);

  // Row 6 (3rd of 6): B8 starts (span 4, 2 cols), B9 starts (span 2), B11 starts (span 4), B6 starts (span 2), D1 continues, C3-C4 start (span 2 each)
  tableBody.push([
    emptyCell(),
    emptyCell(),
    createBoxCell('B8', undefined, 4, 2),  // B8 starts, spans 4 rows, 2 cols wide
    placeholder(),  // B8 colspan
    createBoxCell('B9', undefined, 2),  // B9 starts, spans 2 rows
    createBoxCell('B11', undefined, 4),  // B11 starts, spans 4 rows
    emptyCell(),
    createBoxCell('B6', undefined, 2),  // B6 starts, spans 2 rows
    placeholder(),  // D1 continues
    emptyCell(),
    createBoxCell('C3', undefined, 2),  // C3 starts, spans 2 rows
    createBoxCell('C4', undefined, 2)   // C4 starts, spans 2 rows
  ]);

  // Row 7 (4th of 6): B8 continues, B9 continues, B11 continues, B6 continues, D2 starts (span 3), C3-C4 continue
  tableBody.push([
    emptyCell(),
    emptyCell(),
    placeholder(),  // B8 continues
    placeholder(),  // B8 continues
    placeholder(),  // B9 continues
    placeholder(),  // B11 continues
    emptyCell(),
    placeholder(),  // B6 continues
    createBoxCell('D2', undefined, 3),  // D2 starts, spans 3 rows
    emptyCell(),
    placeholder(),  // C3 continues
    placeholder()   // C4 continues
  ]);

  // Row 8 (5th of 6): B8 continues, B10 starts (span 2), B11 continues, B7 starts (span 2), D2 continues
  tableBody.push([
    emptyCell(),
    emptyCell(),
    placeholder(),  // B8 continues
    placeholder(),  // B8 continues
    createBoxCell('B10', undefined, 2),  // B10 starts, spans 2 rows
    placeholder(),  // B11 continues
    emptyCell(),
    createBoxCell('B7', undefined, 2),  // B7 starts, spans 2 rows
    placeholder(),  // D2 continues
    emptyCell(),
    emptyCell(),
    emptyCell()
  ]);

  // Row 9 (6th of 6): B10 continues, B11 continues, B7 continues, D2 continues, Food (1 row, 3 cols)
  tableBody.push([
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    placeholder(),  // B10 continues
    placeholder(),  // B11 continues
    emptyCell(),
    placeholder(),  // B7 continues
    placeholder(),  // D2 continues
    specialCell('Futtermittel', '#2196f3', 1, 3),  // Food: 1 row, 3 cols (10-12)
    placeholder(),  // Food colspan
    placeholder()   // Food colspan
  ]);

  // Row 10: O1-O4 (O1-O2: Offenstall, O3-O4: Lamastall)
  tableBody.push([
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    createBoxCell('O1'),  // Offenstall - green
    createBoxCell('O2'),  // Offenstall - green
    createBoxCell('O3'),  // Lamastall - brown
    createBoxCell('O4')   // Lamastall - brown
  ]);

  return {
    table: {
      widths: Array(12).fill('*'),  // 12 columns to match the grid
      body: tableBody,
      heights: (rowIndex: number) => {
        // All rows get uniform height to fit on one page
        // Total 10 rows:
        // Rows 0-1: A1-A12 (span 2 rows)
        // Row 2: Spacer
        // Rows 3-8: B/C/D/Treppe/Food block (6 rows)
        // Row 9: O1-O4
        return 27; // Uniform height for all rows
      }
    },
    layout: {
      paddingLeft: () => 1,
      paddingRight: () => 1,
      paddingTop: () => 1,
      paddingBottom: () => 1,
      hLineWidth: () => 1,
      vLineWidth: () => 1
    }
  };
}

function createOwnerLegend(ownerNumberMap: Map<string, string>): any[] {
  const legend: Array<{ number: string; name: string }> = [];
  ownerNumberMap.forEach((number, name) => {
    legend.push({ number, name });
  });

  const sortedLegend = legend.sort((a, b) => parseInt(a.number) - parseInt(b.number));

  // Split into two columns
  const midpoint = Math.ceil(sortedLegend.length / 2);
  const leftColumn = sortedLegend.slice(0, midpoint);
  const rightColumn = sortedLegend.slice(midpoint);

  const rows: any[] = [];
  for (let i = 0; i < midpoint; i++) {
    const leftEntry = leftColumn[i];
    const rightEntry = rightColumn[i];

    const row = [
      { text: leftEntry.number, bold: true, fontSize: 8 },
      { text: leftEntry.name, fontSize: 8, margin: [3, 0, 10, 0] }
    ];

    if (rightEntry) {
      row.push(
        { text: rightEntry.number, bold: true, fontSize: 8 },
        { text: rightEntry.name, fontSize: 8, margin: [3, 0, 0, 0] }
      );
    } else {
      row.push(
        { text: '', fontSize: 8, margin: [0, 0, 0, 0] },
        { text: '', fontSize: 8, margin: [0, 0, 0, 0] }
      );
    }

    rows.push(row);
  }

  return rows;
}
